/**
 * @file The one human-readable renderer for a chunk's provenance. A mapping
 * slice's source is a `ChunkSource` (a sheet, a JSON/XML node, a markdown
 * section, a PDF page, an email body/attachment); this turns it into the
 * subtitle the review UI shows under the section selector and the
 * table-identity header — so the user reads "Sheet 'Order Lines' (#2)" instead
 * of the raw discriminated union. Shared by `MappingReviewChunkNav` (section
 * selector) and `MappingReviewTables` (per-table heading); recurses for the
 * email-attachment wrapper.
 */
import type { ChunkSource } from '#types';

export function mappingReviewDescribeSource(source: ChunkSource): string {
  switch (source.kind) {
    case 'sheet':
      return `Sheet "${source.name}" (#${source.index + 1})`;
    case 'jsonNode':
      return `JSON node ${source.path}`;
    case 'xmlNode':
      return `XML node ${source.path}`;
    case 'markdownSection':
      return source.precedingHeading
        ? `Markdown section #${source.ordinal} under "${source.precedingHeading}"`
        : `Markdown section #${source.ordinal}`;
    case 'pdfChunk':
    case 'pdfPage':
      return `PDF page ${source.page}`;
    case 'textFile':
      return 'Text file';
    case 'emailBody':
      return 'Email body';
    case 'emailAttachment':
      return `Email attachment "${source.filename}" → ${mappingReviewDescribeSource(source.nested)}`;
    case 'synthesizedLineItems':
      return `Line items interpreted from ${mappingReviewDescribeSource(source.from)}`;
  }
}
